
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { translate } from "../common/translations";
import { ClientUserProfileService, ClientUserService, JwtAuthService } from "../services";
import { AppState, store } from "./store";

import { AppUser, TokenPermissions, UnauthorizedError, UserProfile } from "@nt-portal/server-sdk";
import { MessageType, showMessageBox } from "./message-box.slice";
import { decompressBase64 } from "../common";
import { FbxLog } from "../common/utils/logging.utils";


export const initUserProfile = () => {
    let userProfile: UserProfile = {
        user: {
            id: "",
            userName: "",
            name: "",
            email: "",
            sub: null,
            passwordHash: "",
            lockedOut: false,
            lastLoggedIn: null,
            loginAttempts: 0,
            isAdmin: false,
            roles: null,
            permissions: null,
            createdBy: ""
        },
        jobTitle: "",
        jobDescription: "",
        department: "",
        province: "",
        cellPhone: "",
        officePhone: "",
        officeLocation: "",
        createdBy: ""
    }
    return userProfile;
}

export interface CurrentUserState {
    readonly user: AppUser | null;
    readonly settings?: any | null;
    readonly permissions: TokenPermissions;
    readonly roles: string[];
    readonly busy: boolean;
    readonly loadLog: string[];
    readonly initialized: boolean;
    readonly profile?: UserProfile | null;
}

export const initCurrentUserState: CurrentUserState = {
    user: null,
    permissions: {},
    settings: null,
    busy: false,
    loadLog: [],
    roles: [],
    initialized: false,
    profile: null
}

const isInit = (state: CurrentUserState) => {
    let hasUser = state.user?.id !== '';

    return hasUser;
}

export const getUserRights = () => {

    let userToken = JwtAuthService.decodeToken();
    let permission = decompressBase64(userToken?.permission as string) as TokenPermissions;
    return {
        permissions: permission as TokenPermissions,
        roles: userToken?.roles as string[],
    }


}


export const updateProfile = createAsyncThunk(
    'user/updateProfile',
    async (profile: UserProfile, { getState }) => {
        const state = getState() as AppState;
        let currentUser = state.currentUser;
        if (currentUser.user && currentUser.user?.id) {
            let service = new ClientUserProfileService();
            return await service.update(profile)
        }
    }
)

export const getCurrentUser = createAsyncThunk(
    'user/getCurrentUser',
    async (arg, { getState }) => {
        const state = getState() as AppState;
        let currentUser = state.currentUser;
        if (!currentUser.user) {
            FbxLog.log("IsAuthenticated, loading current user from server");
            let service = new ClientUserService();
            try {
                const user = await service.getMe();
                if (user) {
                    const rights = getUserRights();
                    // should i get profile here?
                    FbxLog.log("Current user loaded from server", user);
                    return {
                        user: user, ...rights
                    }

                }
            } catch (ex: unknown) {
                if (ex instanceof UnauthorizedError) {
                    return {
                        user: currentUser.user,

                    }
                } else if (ex instanceof Error) {
                    store.dispatch(showMessageBox({ message: ex.message, messageType: MessageType.Error, duration: 5000, position: "top-center" }))
                }
            } finally {

            }
        }
        return {
            user: currentUser.user,
            permissions: currentUser.permissions,
            roles: currentUser.roles

        }
    })


export const getUserProfile = createAsyncThunk(
    'user/getProfile',
    async (arg, { getState }) => {
        const state = getState() as AppState;
        let currentUser = state.currentUser;
        if (currentUser?.user?.id) {
            let service = new ClientUserProfileService();
            let profile = await service.getMe();
            return {
                profile: profile
            }
        }
    }
)


const currentUserModelSlice = createSlice({
    name: 'current-user-model',
    initialState: initCurrentUserState,
    reducers: {
        setCurrentUser(state, action) {
            state.user = action.payload;
        },

        clearCurrentUser(state, action) {
            const clearState = { ...initCurrentUserState }
            state.user = clearState.user
            state.permissions = clearState.permissions;
            state.profile = null;
            state.roles = clearState.roles
            state.initialized = false;
            state.settings = null;
            state.loadLog = [];
            state.busy = false;
        },

        setCurrentUserBusy(state, action) {
            state.busy = action.payload;
        }

    },
    extraReducers: (builder) => {

        builder.addCase(getCurrentUser.pending, (state, action) => {
            state.busy = true;
        })
        builder.addCase(getCurrentUser.fulfilled, (state, action) => {
            state.user = action.payload.user;
            state.permissions = action.payload.permissions ?? {};
            state.roles = action.payload.roles ?? [];
            let loadLog = [...state.loadLog];
            loadLog.push(translate("LOADED_CURRENT_USER"));
            state.loadLog = loadLog;
            state.initialized = isInit(state)
        })
        builder.addCase(getCurrentUser.rejected, (state, action) => {
            state.busy = false;
            throw action.error;
        })

        builder.addCase(getUserProfile.pending, (state, action) => {
            state.busy = true;
        })
        builder.addCase(getUserProfile.fulfilled, (state, action) => {
            state.profile = action.payload?.profile;
        })
        builder.addCase(getUserProfile.rejected, (state, action) => {
            state.busy = false;
            throw action.error;
        })


        builder.addCase(updateProfile.pending, (state, action) => {
            state.busy = true;
        })
        builder.addCase(updateProfile.fulfilled, (state, action) => {
            let user = { ...state.user } as AppUser;
            state.user = user;
        })
        builder.addCase(updateProfile.rejected, (state, action) => {
            state.busy = false;
            throw action.error;
        })

    }
})


export const { setCurrentUser, setCurrentUserBusy, clearCurrentUser } = currentUserModelSlice.actions
export default currentUserModelSlice.reducer